// src/lib/notify.js
// Sends roster activity to Discord via our own serverless endpoint.
//
// The webhook URL lives server-side (see netlify/functions/notify-discord.js)
// so it never appears in the browser bundle.
//
// Notifications are a convenience, never a requirement: every call here is
// fire-and-forget and swallows its errors, so a Discord outage or a missing
// configuration can't stop someone saving a player.

const ENDPOINT = "/.netlify/functions/notify-discord";

async function post(payload) {
  try {
    await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch {
    // Offline, running locally without Netlify, or Discord is down.
    // Nothing here is worth interrupting the user for.
  }
}

const DIVISION_LABEL = (d) => d || "Unassigned";

/** A player was saved as a draft. */
export function notifyDraftSaved(player) {
  return post({
    title: `Draft saved — ${player.name}`,
    description: "Saved as a draft. Not visible on the site yet.",
    state: "draft",
    imageUrl: player.imageUrl,
    fields: [
      { name: "Section", value: DIVISION_LABEL(player.division) },
      { name: "Status", value: "Draft" },
    ],
  });
}

/** A player went live immediately. */
export function notifyPublished(player) {
  return post({
    title: `Published — ${player.name}`,
    description: "This player is now live on the website.",
    state: "published",
    imageUrl: player.imageUrl,
    fields: [
      { name: "Section", value: DIVISION_LABEL(player.division) },
      { name: "Status", value: "Live now" },
    ],
  });
}

/** A player was scheduled to appear later. */
export function notifyScheduled(player, whenMs) {
  const when = new Date(whenMs).toLocaleString(undefined, {
    weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit",
  });
  return post({
    title: `Scheduled — ${player.name}`,
    description: `Will appear on the website automatically on ${when}.`,
    state: "scheduled",
    imageUrl: player.imageUrl,
    fields: [
      { name: "Section", value: DIVISION_LABEL(player.division) },
      { name: "Goes live", value: when },
    ],
  });
}

/** A player was removed. */
export function notifyRemoved(player) {
  return post({
    title: `Removed — ${player.name}`,
    description: "This player has been taken off the website.",
    state: "removed",
    fields: [{ name: "Section", value: DIVISION_LABEL(player.division) }],
  });
}

/**
 * Pick the right message for whatever was just saved.
 * `isEdit` only changes the wording, not which message is sent.
 */
export function notifySaved(player, { isEdit = false } = {}) {
  if (player.status !== "published") return notifyDraftSaved(player);
  if (player.publishAt && player.publishAt > Date.now()) {
    return notifyScheduled(player, player.publishAt);
  }
  return notifyPublished(player, { isEdit });
}

// ─── Announcements ───────────────────────────────────────────────────────────

const TYPE_LABEL = (t) => (t === "tournament" ? "Tournament" : "News post");

/** An announcement was saved (draft, live, or scheduled). */
export function notifyAnnouncementSaved(item) {
  const scheduled = item.publishAt && item.publishAt > Date.now();
  const state =
    item.status !== "published" ? "draft" : scheduled ? "scheduled" : "published";
  const description =
    state === "draft"
      ? "Saved as a draft. Not visible on the site yet."
      : state === "scheduled"
      ? `Will appear on the Announcements page automatically on ${new Date(
          item.publishAt
        ).toLocaleString(undefined, {
          weekday: "short", day: "numeric", month: "short",
          hour: "2-digit", minute: "2-digit",
        })}.`
      : "This is now live on the Announcements page.";
  return post({
    title: `${state === "draft" ? "Draft saved" : state === "scheduled" ? "Scheduled" : "Published"} — ${item.title}`,
    description,
    state,
    fields: [
      { name: "Type", value: TYPE_LABEL(item.type) },
      ...(item.featured ? [{ name: "Featured", value: "Yes — shown at the top" }] : []),
    ],
  });
}

/** An announcement was removed. */
export function notifyAnnouncementRemoved(item) {
  return post({
    title: `Removed — ${item.title}`,
    description: "This has been taken off the Announcements page.",
    state: "removed",
    fields: [{ name: "Type", value: TYPE_LABEL(item.type) }],
  });
}
